<script setup>
import { computed, nextTick, onMounted, reactive, ref } from 'vue';
import { useAlert } from 'dashboard/composables';
import ZapiWootAPI from 'dashboard/api/zapiwoot';

const isLoading = ref(false);
const isSavingSignature = ref(false);
const privateNoteTemplateRef = ref(null);

const data = reactive({
  channels: [],
});

const form = reactive({
  routing_channel_id: '',
});

const signature = reactive({
  agent_name_prefix_enabled: false,
  agent_name_prefix: '',
  agent_name_suffix_enabled: false,
  agent_name_suffix: '',
  agent_name_display_type: 'agent_name',
  agent_name_custom: '',
  agent_label_position: 'first_line',
  agent_label_separator: ':\n',
  private_note_enabled: true,
  private_note_template:
    '[Mensagem enviada ao contato por {{agent_label}}]\n{{message}}',
});

const previewValues = {
  agent_name: 'Geraldo Silva',
  agent_display_name: 'Gê',
  agent_email: 'geraldo@gammacrucis.com',
  department: 'Comercial',
  message: 'Olá, como posso ajudar?',
  conversation_id: '35',
  inbox_id: '4',
  channel_name: 'Gamma Crucis',
  channel_phone: '+55 11 99999-0000',
  timestamp: '2026-06-07 14:30',
};

const privateNotePlaceholders = [
  {
    token: '{{agent_label}}',
    label: 'Etiqueta do agente',
    description: 'Nome final com fonte, prefixo e sufixo aplicados.',
  },
  {
    token: '{{agent_display_name}}',
    label: 'Nome público',
    description: 'Nome configurado no relacionamento agente/setor.',
  },
  {
    token: '{{agent_name}}',
    label: 'Nome Chatwoot',
    description: 'Nome original do usuário/agente no Chatwoot.',
  },
  {
    token: '{{agent_email}}',
    label: 'E-mail do agente',
    description: 'E-mail do usuário/agente que enviou a mensagem.',
  },
  {
    token: '{{department}}',
    label: 'Setor',
    description: 'Departamento resolvido para a conversa.',
  },
  {
    token: '{{message}}',
    label: 'Mensagem',
    description: 'Texto enviado ao contato no WhatsApp.',
  },
  {
    token: '{{conversation_id}}',
    label: 'Conversa',
    description: 'ID da conversa onde a nota será criada.',
  },
  {
    token: '{{inbox_id}}',
    label: 'Inbox',
    description: 'ID da inbox onde a nota será criada.',
  },
  {
    token: '{{channel_name}}',
    label: 'Canal',
    description: 'Nome do canal WhatsApp configurado.',
  },
  {
    token: '{{channel_phone}}',
    label: 'Telefone do canal',
    description: 'Número do canal WhatsApp, quando cadastrado.',
  },
  {
    token: '{{timestamp}}',
    label: 'Data/hora',
    description: 'Data e hora em que a nota foi formatada.',
  },
];

const selectedChannel = computed(() =>
  data.channels.find(channel => Number(channel.id) === Number(form.routing_channel_id))
);

const previewTemplateValues = computed(() => ({
  ...previewValues,
  channel_name: selectedChannel.value?.name || previewValues.channel_name,
  channel_phone: selectedChannel.value?.phone || previewValues.channel_phone,
}));

const applyChannelSignature = channel => {
  if (!channel) return;

  signature.agent_name_prefix_enabled = !!Number(channel.agent_name_prefix_enabled || 0);
  signature.agent_name_prefix = channel.agent_name_prefix || '';
  signature.agent_name_suffix_enabled = !!Number(channel.agent_name_suffix_enabled || 0);
  signature.agent_name_suffix = channel.agent_name_suffix || '';
  signature.agent_name_display_type = channel.agent_name_display_type || 'agent_name';
  signature.agent_name_custom = channel.agent_name_custom || '';
  signature.agent_label_position = channel.agent_label_position || 'first_line';
  signature.agent_label_separator = channel.agent_label_separator || ':\n';
  signature.private_note_enabled = Number(channel.private_note_enabled ?? 1) === 1;
  signature.private_note_template =
    channel.private_note_template ||
    '[Mensagem enviada ao contato por {{agent_label}}]\n{{message}}';
};

const loadSettings = async () => {
  isLoading.value = true;
  try {
    const response = await ZapiWootAPI.get();
    const payload = response.data || {};

    if (payload.error) {
      useAlert(payload.error);
      return;
    }

    data.channels = payload.channels || [];

    if (!form.routing_channel_id && data.channels.length) {
      form.routing_channel_id = data.channels[0].id;
    }

    applyChannelSignature(selectedChannel.value || data.channels[0]);
  } catch (error) {
    useAlert('Erro ao carregar configurações do ZapiWoot.');
  } finally {
    isLoading.value = false;
  }
};

const onChannelChange = () => {
  applyChannelSignature(selectedChannel.value);
};

const saveSignatureSettings = async () => {
  if (!form.routing_channel_id) {
    useAlert('Selecione um canal.');
    return;
  }

  isSavingSignature.value = true;
  try {
    const response = await ZapiWootAPI.updateChannelSignature({
      routing_channel_id: form.routing_channel_id,
      agent_name_prefix_enabled: signature.agent_name_prefix_enabled,
      agent_name_prefix: signature.agent_name_prefix,
      agent_name_suffix_enabled: signature.agent_name_suffix_enabled,
      agent_name_suffix: signature.agent_name_suffix,
      agent_name_display_type: signature.agent_name_display_type,
      agent_name_custom: signature.agent_name_custom,
      agent_label_position: signature.agent_label_position,
      agent_label_separator: signature.agent_label_separator,
      private_note_enabled: signature.private_note_enabled,
      private_note_template: signature.private_note_template,
    });

    if (response.data?.error) {
      useAlert(response.data.error);
      return;
    }

    useAlert('Formato de mensagens salvo com sucesso.');
    await loadSettings();
  } catch (error) {
    useAlert('Erro ao salvar formato de mensagens.');
  } finally {
    isSavingSignature.value = false;
  }
};

const previewAgentBaseName = computed(() => {
  if (signature.agent_name_display_type === 'department') {
    return previewTemplateValues.value.department;
  }
  if (signature.agent_name_display_type === 'custom') {
    return signature.agent_name_custom || 'Nome customizado';
  }
  return previewTemplateValues.value.agent_display_name;
});

const previewAgentLabel = computed(() => {
  const prefix = signature.agent_name_prefix_enabled
    ? signature.agent_name_prefix || ''
    : '';
  const suffix = signature.agent_name_suffix_enabled
    ? signature.agent_name_suffix || ''
    : '';

  return `${prefix}${previewAgentBaseName.value}${suffix}`;
});

const previewPublicMessage = computed(() => {
  const message = previewTemplateValues.value.message;
  const label = previewAgentLabel.value;
  const separator = signature.agent_label_separator || '';

  if (signature.agent_label_position === 'none') return message;
  if (signature.agent_label_position === 'after_message') {
    return `${message}${separator}${label}`;
  }

  return `${label}${separator}${message}`;
});

const previewPrivateNote = computed(() => {
  if (!signature.private_note_enabled) return 'Notas privadas desativadas.';

  const values = {
    ...previewTemplateValues.value,
    agent_label: previewAgentLabel.value,
  };

  return privateNotePlaceholders.reduce(
    (template, placeholder) =>
      template.replaceAll(
        placeholder.token,
        values[placeholder.token.replace(/[{}]/g, '')] || ''
      ),
    signature.private_note_template || ''
  );
});

const insertPrivateNotePlaceholder = async placeholder => {
  const token = placeholder.token;
  const template = signature.private_note_template || '';
  const textarea = privateNoteTemplateRef.value;

  if (!textarea) {
    signature.private_note_template = `${template}${token}`;
    return;
  }

  const start = textarea.selectionStart ?? template.length;
  const end = textarea.selectionEnd ?? template.length;
  signature.private_note_template =
    template.slice(0, start) + token + template.slice(end);

  await nextTick();
  textarea.focus();
  textarea.setSelectionRange(start + token.length, start + token.length);
};

onMounted(loadSettings);
</script>

<template>
  <div class="flex flex-col gap-6 p-6">
    <div>
      <h1 class="text-2xl font-semibold text-n-slate-12">
        WhatsApp routing and message format
      </h1>
      <p class="mt-1 text-sm text-n-slate-11">
        Configure WhatsApp channels, contact-facing message labels, and private-note formatting.
      </p>
    </div>

    <div
      v-if="isLoading"
      class="rounded-lg border border-n-weak bg-n-alpha-2 p-4 text-sm text-n-slate-11"
    >
      Carregando configurações...
    </div>

    <template v-else>
      <section class="grid gap-4 rounded-lg border border-n-weak bg-n-alpha-2 p-4">
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            Canal
          </h2>
          <p class="text-sm text-n-slate-11">
            Escolha o número/canal WhatsApp que será configurado.
          </p>
        </div>

        <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
          Canal / número WhatsApp
          <select
            v-model="form.routing_channel_id"
            class="rounded-md border border-n-weak bg-n-background p-2"
            @change="onChannelChange"
          >
            <option
              v-for="channel in data.channels"
              :key="channel.id"
              :value="channel.id"
            >
              {{ channel.name }}{{ channel.phone ? ` - ${channel.phone}` : '' }}
            </option>
          </select>
        </label>
      </section>

      <section class="grid gap-5 rounded-lg border border-n-weak bg-n-alpha-2 p-4">
        <div>
          <h2 class="text-lg font-semibold text-n-slate-12">
            Formato das mensagens ao contato
          </h2>
          <p class="text-sm text-n-slate-11">
            Defina a identidade que o contato vê e a nota privada que outros agentes recebem.
          </p>
        </div>

        <div class="grid gap-5 border-t border-n-weak pt-4">
          <div>
            <h3 class="text-sm font-semibold text-n-slate-12">
              Nome exibido ao contato
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              Esta etiqueta é aplicada somente às mensagens públicas enviadas ao WhatsApp.
            </p>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
              Origem do nome
              <select
                v-model="signature.agent_name_display_type"
                class="rounded-md border border-n-weak bg-n-background p-2"
              >
                <option value="agent_name">Nome público do agente</option>
                <option value="department">Departamento</option>
                <option value="custom">Texto customizado</option>
              </select>
              <span class="text-xs font-normal text-n-slate-10">
                "Nome público" vem do cadastro agente/setor abaixo.
              </span>
            </label>

            <label
              v-if="signature.agent_name_display_type === 'custom'"
              class="flex flex-col gap-1 text-sm font-medium text-n-slate-12"
            >
              Nome customizado ao contato
              <input
                v-model="signature.agent_name_custom"
                class="rounded-md border border-n-weak bg-n-background p-2"
                placeholder="Ex: Atendimento Gamma"
              />
            </label>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <div class="grid gap-2">
              <label class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
                <input v-model="signature.agent_name_prefix_enabled" type="checkbox" />
                Prefixo no nome
              </label>
              <input
                v-model="signature.agent_name_prefix"
                class="w-full rounded-md border border-n-weak bg-n-background p-2"
                placeholder="Ex: ["
              />
            </div>

            <div class="grid gap-2">
              <label class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
                <input v-model="signature.agent_name_suffix_enabled" type="checkbox" />
                Sufixo no nome
              </label>
              <input
                v-model="signature.agent_name_suffix"
                class="w-full rounded-md border border-n-weak bg-n-background p-2"
                placeholder="Ex: ]"
              />
            </div>
          </div>

          <div>
            <h4 class="mb-2 text-xs font-semibold uppercase text-n-slate-10">
              Etiqueta resultante
            </h4>
            <pre class="whitespace-pre-wrap rounded-md border border-n-weak bg-n-background p-3 text-sm">{{ previewAgentLabel }}</pre>
          </div>
        </div>

        <div class="grid gap-5 border-t border-n-weak pt-4">
          <div>
            <h3 class="text-sm font-semibold text-n-slate-12">
              Mensagem pública no WhatsApp
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              Controle onde a etiqueta aparece em relação ao texto enviado pelo agente.
            </p>
          </div>

          <div class="grid gap-4 md:grid-cols-2">
            <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
              Posição da etiqueta
              <select
                v-model="signature.agent_label_position"
                class="rounded-md border border-n-weak bg-n-background p-2"
              >
                <option value="none">Não mostrar</option>
                <option value="before_message">Antes da mensagem</option>
                <option value="first_line">Primeira linha</option>
                <option value="after_message">Depois da mensagem</option>
              </select>
            </label>

            <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
              Separador entre etiqueta e mensagem
              <textarea
                v-model="signature.agent_label_separator"
                class="min-h-20 rounded-md border border-n-weak bg-n-background p-2 font-mono"
                placeholder="Ex: :\n"
              />
              <span class="text-xs font-normal text-n-slate-10">
                Pode conter quebras de linha.
              </span>
            </label>
          </div>

          <div>
            <h4 class="mb-2 text-xs font-semibold uppercase text-n-slate-10">
              Prévia para o contato
            </h4>
            <pre class="whitespace-pre-wrap rounded-md border border-n-weak bg-n-background p-3 text-sm">{{ previewPublicMessage }}</pre>
          </div>
        </div>

        <div class="grid gap-5 border-t border-n-weak pt-4">
          <div>
            <h3 class="text-sm font-semibold text-n-slate-12">
              Nota privada para outros agentes
            </h3>
            <p class="mt-1 text-sm text-n-slate-11">
              A nota é criada nas outras conversas ativas do mesmo contato.
            </p>
          </div>

          <label class="flex items-center gap-2 text-sm font-medium text-n-slate-12">
            <input v-model="signature.private_note_enabled" type="checkbox" />
            Criar nota privada
          </label>

          <label class="flex flex-col gap-1 text-sm font-medium text-n-slate-12">
            Template da nota privada
            <textarea
              ref="privateNoteTemplateRef"
              v-model="signature.private_note_template"
              class="min-h-32 rounded-md border border-n-weak bg-n-background p-2 font-mono"
            />
          </label>

          <div>
            <h4 class="mb-2 text-xs font-semibold uppercase text-n-slate-10">
              Variáveis disponíveis
            </h4>
            <div class="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
              <button
                v-for="placeholder in privateNotePlaceholders"
                :key="placeholder.token"
                type="button"
                class="rounded-md border border-n-weak bg-n-background p-3 text-left hover:bg-n-alpha-2"
                @click="insertPrivateNotePlaceholder(placeholder)"
              >
                <span class="block font-mono text-xs font-semibold text-n-brand">
                  {{ placeholder.token }}
                </span>
                <span class="mt-1 block text-sm font-medium text-n-slate-12">
                  {{ placeholder.label }}
                </span>
                <span class="mt-1 block text-xs text-n-slate-10">
                  {{ placeholder.description }}
                </span>
              </button>
            </div>
          </div>

          <div>
            <h4 class="mb-2 text-xs font-semibold uppercase text-n-slate-10">
              Prévia da nota privada
            </h4>
            <pre class="whitespace-pre-wrap rounded-md border border-n-weak bg-n-background p-3 text-sm">{{ previewPrivateNote }}</pre>
          </div>
        </div>

        <div>
          <button
            type="button"
            class="rounded-md bg-n-brand px-4 py-2 text-sm font-semibold text-white disabled:opacity-60"
            :disabled="isSavingSignature"
            @click="saveSignatureSettings"
          >
            {{ isSavingSignature ? 'Salvando...' : 'Salvar formato das mensagens' }}
          </button>
        </div>
      </section>

    </template>
  </div>
</template>
